import { ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Header from './components/header';
import './App.css';
import { useAuth } from './hooks/useAuth';
import { useCapabilities } from './hooks/useCapabilities';
import { useIsCommissionGroupManager } from './hooks/useIsCommissionGroupManager';
import { CAPABILITIES } from './helpers/capabilities';
import LoginPage from './pages/login/LoginPage';
import ForgotPasswordPage from './pages/login/ForgotPasswordPage';
import NotFound from './pages/notfound/index';
import AccessDenied from './pages/accessdenied/index';
import AuditLogsPage from './pages/auditlogs/index';
import ResetPasswordModal from './modals/ResetPasswordModal';
import SetupPaymentPage from './pages/setup-payment/SetupPaymentPage';
import ConnectOnboardingPage from './pages/connect-onboarding/ConnectOnboardingPage';
import VenuesPage from './pages/venues/index';
import EditVenuePage from './pages/venues/EditVenuePage';
import ImportVenuePage from './pages/venues/ImportVenuePage';
import UsersPage from './pages/users/index';
import EditUserPage from './pages/users/EditUserPage';
import ShowsPage from './pages/shows/index';
import EditShowPage from './pages/shows/EditShowPage';
import TicketOptionsPage from './pages/ticketoptions/index';
import EditTicketOptionPage from './pages/ticketoptions/EditTicketOptionPage';
import TicketTypesPage from './pages/tickettypes/index';
import EditTicketTypePage from './pages/tickettypes/EditTicketTypePage';
import TimeslotsPage from './pages/timeslots/index';
import EditTimeslotPage from './pages/timeslots/EditTimeslotPage';
import BulkAddTimeslotsPage from './pages/timeslots/BulkAddTimeslotsPage';
import CreateRentalDayPage from './pages/timeslots/CreateRentalDayPage';
import RentalDayEditorPage from './pages/timeslots/RentalDayEditorPage';
import BulkAddRentalDaysPage from './pages/timeslots/BulkAddRentalDaysPage';
import SeatAssignmentPage from './pages/timeslots/SeatAssignmentPage';
import ProfilePage from './pages/me/index';
import BookingsPage from './pages/bookings/index';
import EditBookingPage from './pages/bookings/EditBookingPage';
import CRMPage from './pages/crm/index';
import CateringViewPage from './pages/cateringview/index';
import CateringViewSelector from './pages/cateringview/CateringViewSelector';
import SeatEditorPage from './pages/seats/index';
import CheckinViewPage from './pages/checkinview/index';
import CheckinViewSelector from './pages/checkinview/CheckinViewSelector';
import PaymentMethodsPage from './pages/paymentmethods/index';
import EditPaymentMethodPage from './pages/paymentmethods/EditPaymentMethodPage';
import EmailLogsPage from './pages/emaillogs/index';
import ManualRefundsPage from './pages/manualrefunds/index';
import IntegrationsPage from './pages/integrations/index';
import EditIntegrationPage from './pages/integrations/EditIntegrationPage';
import VenueIntegrationsPage from './pages/venueintegrations/index';
import EditVenueIntegrationPage from './pages/venueintegrations/EditVenueIntegrationPage';
import CouponsPage from './pages/coupons/index';
import AddCouponsPage from './pages/coupons/AddCouponsPage';
import ReportsPage from './pages/reports/index';
import InvoicesPage from './pages/invoices/index';
import CommissionGroupsPage from './pages/commissiongroups/index';
import EditCommissionGroupPage from './pages/commissiongroups/EditCommissionGroupPage';
import WaitlistPage from './pages/waitlist/index';
import UpgradesPage from './pages/upgrades/index';
import EmailTesterPage from './pages/emailtester/index';
import CommissionGroupManagement from './pages/commissiongroupmanagement/CommissionGroupManagement';

interface PageProps {
    children: ReactNode;
    capability: string | string[] | null;
    superadminOnly?: boolean;
    allowManager?: boolean;
}

const C = CAPABILITIES;

export const ROUTES = [
    {
        path: '/commission-group-management',
        backUrl: false,
        page: <CommissionGroupManagement />,
        // Venue staff reach it via the cap; commission-group managers (who hold
        // no venue cap) are let in by allowManager so non-managers don't land
        // on a blank page.
        capability: C.COMMISSION_GROUPS_MANAGE,
        allowManager: true,
    },
    {
        path: '/venues',
        backUrl: false,
        page: <VenuesPage />,
        capability: [C.VENUES_VIEW, C.USERS_MANAGE, C.PAYMENT_METHODS_VIEW],
    },
    {
        path: '/venues/new',
        backUrl: '/venues',
        page: <EditVenuePage newVenue />,
        capability: C.VENUES_MANAGE,
    },
    {
        path: '/venues/import',
        backUrl: '/venues',
        page: <ImportVenuePage />,
        capability: C.VENUES_MANAGE,
    },
    {
        path: '/venues/edit/:venueId',
        backUrl: '/venues',
        page: <EditVenuePage />,
        capability: C.VENUES_VIEW,
    },
    {
        path: '/shows',
        backUrl: false,
        page: <ShowsPage />,
        capability: [C.SHOWS_VIEW, C.COUPONS_VIEW, C.INTEGRATIONS_MANAGE, C.COMMISSION_GROUPS_VIEW],
    },
    {
        path: '/shows/new',
        backUrl: '/shows',
        page: <EditShowPage newShow />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/edit/:showId',
        backUrl: '/shows',
        page: <EditShowPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/venues/:venueId/users',
        backUrl: '/venues',
        page: <UsersPage />,
        capability: C.USERS_MANAGE,
    },
    {
        path: '/venues/:venueId/users/new',
        backUrl: '/venues/:venueId/users',
        page: <EditUserPage newUser />,
        capability: C.USERS_MANAGE,
    },
    {
        path: '/venues/:venueId/users/edit/:userId',
        backUrl: '/venues/:venueId/users',
        page: <EditUserPage />,
        capability: C.USERS_MANAGE,
    },
    {
        path: '/shows/:showId/ticketoptions',
        backUrl: '/shows',
        page: <TicketOptionsPage />,
        capability: C.SHOWS_VIEW,
    },
    {
        path: '/shows/:showId/ticketoptions/edit/:ticketOptionId',
        backUrl: '/shows',
        page: <EditTicketOptionPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/ticketoptions/new',
        backUrl: '/shows',
        page: <EditTicketOptionPage newTicketOption />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/tickettypes',
        backUrl: '/shows',
        page: <TicketTypesPage />,
        capability: C.SHOWS_VIEW,
    },
    {
        path: '/shows/:showId/seats',
        backUrl: '/shows',
        page: <SeatEditorPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/tickettypes/edit/:ticketTypeId',
        backUrl: '/shows',
        page: <EditTicketTypePage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/tickettypes/new',
        backUrl: '/shows',
        page: <EditTicketTypePage newTicketType />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots',
        backUrl: '/shows',
        page: <TimeslotsPage />,
        capability: C.SHOWS_VIEW,
    },
    {
        path: '/shows/:showId/timeslots/edit/:timeslotId',
        backUrl: '/shows',
        page: <EditTimeslotPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots/:timeslotId/assign-seats',
        backUrl: '/shows',
        page: <SeatAssignmentPage />,
        capability: C.SEATING_ASSIGNMENTS,
    },
    {
        path: '/shows/:showId/timeslots/new',
        backUrl: '/shows',
        page: <EditTimeslotPage newTimeslot />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots/bulk-add',
        backUrl: '/shows',
        page: <BulkAddTimeslotsPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots/rental-day',
        backUrl: '/shows',
        page: <CreateRentalDayPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots/bulk-rental-day',
        backUrl: '/shows',
        page: <BulkAddRentalDaysPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/timeslots/rental-day/:groupId',
        backUrl: '/shows',
        page: <RentalDayEditorPage />,
        capability: C.SHOWS_MANAGE,
    },
    {
        path: '/shows/:showId/coupons',
        backUrl: '/shows',
        page: <CouponsPage />,
        capability: C.COUPONS_VIEW,
    },
    {
        path: '/shows/:showId/coupons/new',
        backUrl: '/shows',
        page: <AddCouponsPage />,
        capability: C.COUPONS_MANAGE,
    },
    {
        path: '/bookings',
        backUrl: false,
        page: <BookingsPage />,
        capability: C.BOOKINGS_VIEW,
    },
    {
        path: '/bookings/edit/:bookingId',
        backUrl: '/bookings',
        page: <EditBookingPage />,
        capability: C.BOOKINGS_VIEW,
    },
    {
        path: '/crm',
        backUrl: false,
        page: <CRMPage />,
        capability: C.BOOKINGS_VIEW,
    },
    {
        path: '/cateringview',
        backUrl: false,
        page: <CateringViewSelector />,
        capability: C.CATERING_VIEW,
    },
    {
        path: '/cateringview/:timeslotId',
        backUrl: '/cateringview',
        page: <CateringViewPage />,
        capability: C.CATERING_VIEW,
    },
    {
        path: '/checkinview',
        backUrl: false,
        page: <CheckinViewSelector />,
        capability: C.CHECKIN_VIEW,
    },
    {
        path: '/checkinview/:timeslotId',
        backUrl: '/checkinview',
        page: <CheckinViewPage />,
        capability: C.CHECKIN_VIEW,
    },
    {
        path: '/user/me',
        backUrl: false,
        page: <ProfilePage />,
        capability: null,
    },
    {
        path: '/auditlogs',
        backUrl: false,
        page: <AuditLogsPage />,
        capability: C.LOGS_VIEW,
    },
    {
        path: '/venues/:venueId/paymentmethods',
        backUrl: '/venues',
        page: <PaymentMethodsPage />,
        capability: C.PAYMENT_METHODS_VIEW,
    },
    {
        path: '/venues/:venueId/paymentmethods/new',
        backUrl: '/venues',
        page: <EditPaymentMethodPage newPaymentMethod />,
        capability: C.PAYMENT_METHODS_MANAGE,
    },
    {
        path: '/venues/:venueId/paymentmethods/edit/:paymentMethodId',
        backUrl: '/venues',
        page: <EditPaymentMethodPage />,
        capability: C.PAYMENT_METHODS_MANAGE,
    },
    {
        path: '/venues/:venueId/integrations',
        backUrl: '/venues',
        page: <VenueIntegrationsPage />,
        capability: C.INTEGRATIONS_MANAGE,
    },
    {
        path: '/venues/:venueId/integrations/:integrationType',
        backUrl: '/venues',
        page: <EditVenueIntegrationPage />,
        capability: C.INTEGRATIONS_MANAGE,
    },
    {
        path: '/emaillogs',
        backUrl: false,
        page: <EmailLogsPage />,
        capability: C.LOGS_VIEW,
    },
    {
        path: '/email-tester',
        backUrl: '/emaillogs',
        page: <EmailTesterPage />,
        capability: null,
        superadminOnly: true,
    },
    {
        path: '/manualrefunds',
        backUrl: false,
        page: <ManualRefundsPage />,
        capability: C.BOOKINGS_REFUND,
    },
    {
        path: '/waitlist',
        backUrl: false,
        page: <WaitlistPage />,
        capability: C.WAITLIST_MANAGE,
    },
    {
        path: '/upgrades',
        backUrl: false,
        page: <UpgradesPage />,
        capability: C.UPGRADE_MANAGE,
    },
    {
        path: '/shows/:showId/integrations',
        backUrl: '/shows',
        page: <IntegrationsPage />,
        capability: C.INTEGRATIONS_MANAGE,
    },
    {
        backUrl: '/shows',
        path: '/shows/:showId/integrations/:integrationType',
        page: <EditIntegrationPage />,
        capability: C.INTEGRATIONS_MANAGE,
    },
    {
        path: '/shows/:showId/commissiongroups',
        backUrl: '/shows',
        page: <CommissionGroupsPage />,
        capability: C.COMMISSION_GROUPS_VIEW,
    },
    {
        path: '/shows/:showId/commissiongroups/new',
        backUrl: '/shows',
        page: <EditCommissionGroupPage newCommissionGroup />,
        capability: C.COMMISSION_GROUPS_MANAGE,
    },
    {
        path: '/shows/:showId/commissiongroups/edit/:commissionGroupId',
        backUrl: '/shows',
        page: <EditCommissionGroupPage />,
        capability: C.COMMISSION_GROUPS_MANAGE,
    },
    {
        path: '/reports',
        backUrl: false,
        page: <ReportsPage />,
        capability: [
            C.REPORTS_SALES_VIEW,
            C.REPORTS_TRANSACTIONS_VIEW,
            C.REPORTS_MANIFEST_VIEW,
            C.REPORTS_COMMISSION_VIEW,
            C.REPORTS_AFFILIATE_SALES_VIEW,
            C.REPORTS_AFFILIATE_INVOICE_VIEW,
        ],
    },
    {
        path: '/invoices',
        backUrl: false,
        page: <InvoicesPage />,
        capability: C.INVOICES_VIEW,
    },
];

function RestrictedPage({ children, capability, superadminOnly, allowManager }: PageProps) {
    const { user } = useAuth();
    const { can, loading } = useCapabilities();
    const { isCommissionGroupManager, isLoading: managerLoading } = useIsCommissionGroupManager();
    if (!user) return <LoginPage />;
    if (loading) return null;
    if (superadminOnly && !user.isSuperAdmin) return <Navigate to="/access-denied" replace />;
    if (capability !== null) {
        // Wait for the manager lookup before deciding, or a manager would be
        // bounced to access-denied on the first render.
        if (allowManager && managerLoading) return null;
        const hasAccess = can(capability) || (allowManager && isCommissionGroupManager);
        if (!hasAccess) return <Navigate to="/access-denied" replace />;
    }
    return <>{children}</>;
}

function App() {
    return (
        <Box sx={{ display: 'flex' }}>
            <Header />
            <Box component="main" my={2} sx={{ flexGrow: 1, minWidth: 0, pt: 8, width: '100%' }}>
                <Routes>
                    <Route path="/" element={<LoginPage />} />
                    <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
                    <Route path="resetpassword/:token" element={<ResetPasswordModal />} />
                    <Route
                        path="/setup-payment/:stripeCustomerId/:hash"
                        element={<SetupPaymentPage />}
                    />
                    <Route
                        path="/connect-onboarding/:venueSlug/:paymentMethodId/:hash"
                        element={<ConnectOnboardingPage />}
                    />
                    <Route path="/not-found" element={<NotFound />} />
                    <Route path="/access-denied" element={<AccessDenied />} />
                    {ROUTES.map((route, idx) => (
                        <Route
                            key={idx}
                            path={route.path}
                            element={
                                <RestrictedPage
                                    capability={route.capability}
                                    superadminOnly={route.superadminOnly}
                                    allowManager={route.allowManager}
                                >
                                    {route.page}
                                </RestrictedPage>
                            }
                        />
                    ))}
                </Routes>
            </Box>
        </Box>
    );
}

export default App;
